import { useState } from "react";
import {
  readFileAsBase64, runCompare,
  type Board, type CompareProjectImpact, type CompareRefusal, type CompareResult,
  type ObjectiveNote,
} from "../api.js";

/**
 * What would change if the optimizer had its way.
 *
 * The rollout question, not a scheduling one: people are being asked to trust
 * a solver with work they currently assign by hand, and the honest answer is a
 * list of exactly which jobs change hands and which projects move. So the
 * summary leads with churn and with dates, and the detail names every phase.
 *
 * Nothing is written. This reads the whole Epicor detail out of the file,
 * schedules it exactly as the file has it, then schedules it again with the
 * optimizer free to move work within each drafting group — no scenario, no
 * phase, no assignment is ever saved.
 *
 * The solver caps itself at ten seconds, and on a backlog this size that cap
 * binds — the run comes back FEASIBLE, not OPTIMAL, and two runs of the same
 * file can propose a different set of moves for the same makespan. That has
 * to be visible here, not left for someone to discover by running it twice
 * and losing trust in the tool.
 */
export function Compare({ board }: { board: Board }) {
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState<CompareRefusal | null>(null);
  const [result, setResult] = useState<CompareResult | null>(null);
  const isAdmin = board.user.role === "admin";

  const nameOf = (id: string | null) => (id ? result?.names[id] ?? id : "unassigned");

  async function compare(f: File) {
    setBusy("Reading the file…"); setErr(null); setResult(null);
    try {
      const contentBase64 = await readFileAsBase64(f);
      setBusy("Holding the file's assignments, then solving freely — two solves, so this takes a moment…");
      setResult(await runCompare(contentBase64, f.name));
    } catch (e) {
      setErr(e as CompareRefusal);
    } finally {
      setBusy(null);
    }
  }

  const s = result?.diff.summary;
  const headline = result ? biggestMove(result.diff.projects) : null;
  const optimizedStatus = result?.solverStatus.optimized ?? null;

  return (
    <div>
      <p className="hint" style={{ marginTop: 0 }}>
        This reads the whole Epicor detail out of the file, schedules it exactly as the
        file has it, then schedules it again with the optimizer free to move work within
        each drafting group — and shows the difference. <b>Nothing is saved. The schedule,
        your scenarios and your projects are untouched.</b>
      </p>
      <p className="hint">
        The file carries assignments, hours and due dates but no schedule of its own, so
        <b> "As in the file" is not the shop's committed plan</b> — it is those same
        assignments, re-dated by the scheduler the same way the optimized column is. Both
        columns are solved the same way, so the comparison between them is fair; the
        absolute finish dates on the left are the tool's, not the shop's.
      </p>
      <p className="hint">
        The optimizer can only move work between people this file already names —
        anyone with no jobs in this export is invisible to it, so the real room to
        manoeuvre may be larger than what is shown below.
      </p>

      {!isAdmin ? (
        <p className="muted">Running a comparison is an admin's decision. You can read any it leaves behind.</p>
      ) : (
        <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 16 }}>
          <input type="file" accept=".xlsx"
            onChange={(e) => { const f = e.target.files?.[0]; if (f) void compare(f); }} />
          {busy && <span className="muted">{busy}</span>}
        </div>
      )}

      {err && (
        <div className="err" style={{ marginBottom: 12 }}>
          {err.message}
          {(err.peakHeldBlocks !== undefined || err.horizonBlocks !== undefined) && (
            <div className="mono" style={{ marginTop: 8, fontSize: 12 }}>
              {err.peakHeldBlocks !== undefined && <>peak load {err.peakHeldBlocks} half-days</>}
              {err.maxBlocks !== undefined && <> of {err.maxBlocks} max</>}
              {err.horizonBlocks !== undefined && <> · horizon built {err.horizonBlocks} half-days</>}
            </div>
          )}
        </div>
      )}

      {result && s && (
        <>
          {/* Empty in the normal case. Non-empty means the objective that ran is not
              quite the one asked for, which would make every number below quietly
              wrong — so this has to lead, ahead of the numbers it undermines. */}
          {!!result.objectiveNotes.length && (
            <div className="run-warn" style={{ marginBottom: 16 }}>
              <b>The solver had to adjust its objective to fit its arithmetic — the
              numbers below may not be the comparison that was asked for:</b>
              {result.objectiveNotes.map((n: ObjectiveNote, i) => (
                <div className="term" key={i}>
                  {n.kind} · weight {n.weight} · {n.action} — {n.detail}
                </div>
              ))}
            </div>
          )}

          {optimizedStatus && (
            <div className="run-absence" style={{ marginBottom: 16 }}>
              {optimizedStatus === "FEASIBLE" ? (
                <>
                  The optimizer stopped at its time limit with a good schedule, not a proven-best
                  one — <b>this is one plan among several of equal value.</b> Re-running the
                  comparison may propose a different set of jobs to move; the finish dates below
                  are the part that repeats.
                </>
              ) : (
                <>The optimizer proved this is the best schedule under the current objective.</>
              )}
            </div>
          )}

          {result.durationDependsOnAssignee && (
            <div className="run-absence" style={{ marginBottom: 16 }}>
              People here carry different capacities, so a task's modeled length depends on
              who is holding it — one person's when the file has them, the group's median when
              the optimizer is free to choose. <b>Part of any "finishes earlier" below is that
              modeling difference, not a real scheduling gain.</b>
            </div>
          )}

          <p className="hint" style={{ marginTop: 0, marginBottom: 4 }}>
            The optimizer would move <b>{s.reassigned}</b> job{s.reassigned === 1 ? "" : "s"} to a
            different person.{headline && (
              <> The biggest single effect: <b>{headline.projectId}</b> finishes{" "}
                <b>{headline.toEnd}</b> instead of <b>{headline.fromEnd}</b> — about{" "}
                {weeksLabel(headline.shiftBlocks)} {headline.shiftBlocks < 0 ? "earlier" : "later"}.</>
            )}
          </p>
          <p className="hint" style={{ marginTop: 0, marginBottom: !result.skipped.length ? 16 : 4, fontSize: 13 }}>
            Comparing <b>{result.summaryCounts.compared}</b> of the <b>{result.summaryCounts.jobsInFile}</b>{" "}
            jobs in the file, across <b>{result.summaryCounts.people}</b> people.
          </p>

          {!!result.skipped.length && (
            <Section title="Not compared" n={result.skipped.length} style={{ marginBottom: 16 }}>
              {groupSkipped(result.skipped).map(([why, ids]) => (
                <p key={why} className="hint mono" style={{ marginTop: 0, marginBottom: 6 }}>
                  <b>{ids.length}</b> {why} — {ids.join(", ")}
                </p>
              ))}
            </Section>
          )}

          <div className="kpis">
            <Kpi label="Change hands" value={s.reassigned} sub="phases the optimizer moves" crit={s.reassigned > 0} />
            <Kpi label="Re-dated" value={s.rescheduled} sub="same person, different days" />
            <Kpi label="Finish earlier" value={s.projectsEarlier} sub="projects" />
            <Kpi label="Finish later" value={s.projectsLater} sub="projects" crit={s.projectsLater > 0} />
            <Kpi label="Hours moved" value={Math.round(s.hoursMoved)} sub="between people" />
          </div>

          {!!result.assumed.length && (
            <Section title="Capacity assumed" n={result.assumed.length}>
              <p className="hint" style={{ marginTop: 0 }}>
                Some of these people are in the file but not set up on People at all; others
                are on People, but with a capacity or FTE the scheduler cannot use — zero or
                missing, often set that way on purpose to mark someone unavailable. Either
                way, this comparison could not use what People has and guessed instead: full
                time at 80%, with no time off. Check People and run it again for a firmer
                answer.
              </p>
              <ul className="mono">{result.assumed.map((n) => <li key={n}>{n}</li>)}</ul>
            </Section>
          )}

          {!!result.markedInactive.length && (
            <Section title="Inactive on People, working in the file" n={result.markedInactive.length}>
              <p className="hint" style={{ marginTop: 0 }}>
                The file still assigns these people work, even though their People record marks
                them inactive. The file is the authority on who is doing the work, so this
                comparison scheduled them anyway — this just means their People record is stale.
              </p>
              <ul className="mono">{result.markedInactive.map((n) => <li key={n}>{n}</li>)}</ul>
            </Section>
          )}

          {!!result.multiDiscipline.length && (
            <Section title="Working in two groups" n={result.multiDiscipline.length}>
              <p className="hint" style={{ marginTop: 0 }}>
                The file puts these people in both mechanical and electrical drafting. A
                person belongs to one group here, so their second group was not considered
                and the optimizer had less room than it really has.
              </p>
              <ul className="mono">{result.multiDiscipline.map((n) => <li key={n}>{n}</li>)}</ul>
            </Section>
          )}

          {!!result.diff.projects.length && (
            <Section title="Projects that move" n={result.diff.projects.length}>
              <table className="qt">
                <thead>
                  <tr>
                    <th>Project</th><th>As in the file</th><th>Optimized</th>
                    <th className="r">Days</th><th className="r">Phases</th>
                  </tr>
                </thead>
                <tbody>
                  {result.diff.projects.map((p) => (
                    <tr key={p.projectId}>
                      <td className="mono"><b>{p.projectId}</b></td>
                      <td className="mono">{p.fromEnd ?? "—"}</td>
                      <td className={`mono ${p.shiftBlocks > 0 ? "late" : ""}`}>{p.toEnd ?? "—"}</td>
                      <td className={`r mono ${p.shiftBlocks > 0 ? "crit" : ""}`}>
                        {p.shiftBlocks > 0 ? `+${p.shiftBlocks}` : p.shiftBlocks}
                      </td>
                      <td className="r">{p.phasesChanged}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Section>
          )}

          <Section title="Every step that changes" n={result.diff.changes.length}>
            <table className="qt" style={{ minWidth: 820 }}>
              <thead>
                <tr>
                  <th>Step</th><th>Project</th><th>As in the file</th><th>Optimized</th>
                  <th className="r">Hours</th><th>What changed</th>
                </tr>
              </thead>
              <tbody>
                {result.diff.changes.map((c) => (
                  <tr key={c.phaseId}>
                    <td className="mono">{c.phaseId}</td>
                    <td className="mono">{c.projectId ?? "—"}</td>
                    <td>{nameOf(c.from.resourceId)}</td>
                    <td><b>{nameOf(c.to.resourceId)}</b></td>
                    <td className="r mono">{Math.round(c.remainingHours * 10) / 10}</td>
                    <td className="muted">{c.kinds.join(", ")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Section>
        </>
      )}
    </div>
  );
}

/**
 * Rows the file had that never became a job to compare, grouped by why.
 *
 * A flat list of job numbers answers "which" and not "so what" — the reason
 * is what tells a reader whether a gap is expected (a row already finished)
 * or something to go check. Largest group first, so the dominant reason
 * reads at a glance.
 */
function groupSkipped(skipped: { phaseId: string; why: string }[]): [string, string[]][] {
  const byWhy = new Map<string, string[]>();
  for (const s of skipped) {
    const ids = byWhy.get(s.why) ?? [];
    ids.push(s.phaseId);
    byWhy.set(s.why, ids);
  }
  return [...byWhy.entries()].sort((a, b) => b[1].length - a[1].length);
}

/** The project whose finish shifted the most — the one number the reader keeps. */
function biggestMove(projects: CompareProjectImpact[]): CompareProjectImpact | null {
  if (!projects.length) return null;
  return projects.reduce((worst, p) =>
    Math.abs(p.shiftBlocks) > Math.abs(worst.shiftBlocks) ? p : worst,
  );
}

/** Half-day blocks, no weekends, 2 per working day → 10 per working week. */
function weeksLabel(blocks: number): string {
  const days = Math.abs(blocks) / 2;
  const weeks = Math.round(days / 5);
  if (weeks >= 2) return `${weeks} working weeks`;
  const wholeDays = Math.round(days);
  return `${wholeDays} working day${wholeDays === 1 ? "" : "s"}`;
}

function Section(
  { title, n, style, children }:
  { title: string; n: number; style?: React.CSSProperties; children: React.ReactNode },
) {
  return (
    <div className="run-sec" style={style}>
      <h3>{title}<span className="run-sec-n">{n}</span></h3>
      <div className="scroll">{children}</div>
    </div>
  );
}

function Kpi({ label, value, sub, crit }: { label: string; value: number; sub: string; crit?: boolean }) {
  return (
    <div className="kpi">
      <span className="lab">{label}</span>
      <span className={`val${crit ? " crit" : ""}`}>{value}</span>
      <span className="sub">{sub}</span>
    </div>
  );
}
